// @core/stores/fiscalization/FiscalizationFiscalReceiptStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { fiscalizationApi } from '@core/api';
import type {
  CreateCorrectionReceiptRequest,
  CreateFiscalReceiptRequest,
  FiscalReceipt,
  RetryFiscalReceiptRequest,
  RetryFiscalReceiptResponse,
} from '@core/types/fiscalization';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class FiscalizationFiscalReceiptStore {
  // List state
  items: FiscalReceipt[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: FiscalReceipt | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, FiscalReceipt>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): FiscalReceipt | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: FiscalReceipt | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create correction receipt
   */
  async CreateCorrectionReceipt(data: CreateCorrectionReceiptRequest): Promise<FiscalReceipt> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.CreateCorrectionReceipt(data);
      const item = response.fiscal_receipt;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create fiscal receipt
   */
  async CreateFiscalReceipt(data: CreateFiscalReceiptRequest): Promise<FiscalReceipt> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.CreateFiscalReceipt(data);
      const item = response.fiscal_receipt;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Get fiscal receipt by ID
   */
  async GetFiscalReceipt(id: string): Promise<FiscalReceipt> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.GetFiscalReceipt(id);
      const item = response.fiscal_receipt;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List fiscal receipts
   */
  async ListFiscalReceipts(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.ListFiscalReceipts(params);

      runInAction(() => {
        this.items = response.fiscal_receipts?.receipts ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Retry failed fiscal receipt
   */
  async RetryFiscalReceipt(id: string, data: RetryFiscalReceiptRequest): Promise<RetryFiscalReceiptResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await fiscalizationApi.RetryFiscalReceipt(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const fiscalizationFiscalReceiptStore = new FiscalizationFiscalReceiptStore();
